import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import type { FlattenedItem, NestedSelectMenuItem } from '#layers/ui/public';
import Fuse from 'fuse.js';
import { getPrimitiveTypeIcon } from '#layers/data-model/utils/icons';
import { prioritizeTopLevelMatches } from '#layers/ui/public';

/** Default traversal depth for field paths — the build/walk cap and the menu's default max nesting. */
export const DEFAULT_FIELD_PATH_MAX_DEPTH = 15;

export type CollectionFieldPath = [
	...GlobalSingleRelationFieldId[],
	GlobalPrimitiveFieldId | GlobalSingleRelationFieldId,
];

export type PrimitiveFieldPath = [
	...GlobalSingleRelationFieldId[],
	GlobalPrimitiveFieldId,
];

export type CollectionFieldPathTerminal = 'primitive' | 'primitive-or-single-relation' | 'primitive-or-relation';

export type CollectionFieldPathForTerminal<TTerminal extends CollectionFieldPathTerminal | undefined> =
	TTerminal extends 'primitive-or-single-relation' | 'primitive-or-relation'
		? CollectionFieldPath
		: PrimitiveFieldPath;

export interface CollectionFieldPathPrimitiveField {
	id: GlobalPrimitiveFieldId;
	apiName: string;
	type: PrimitiveType;
	meta?: { displayName?: string | null } | null;
	constrainedBy?: unknown[] | null;
}

export interface CollectionFieldPathRelationField {
	id: GlobalSingleRelationFieldId;
	apiName: string;
	isList: boolean;
	meta?: { displayName?: string | null } | null;
	oppositeCollection?: CollectionFieldPathCollection | null;
}

export type CollectionFieldPathField = CollectionFieldPathPrimitiveField | CollectionFieldPathRelationField;

export interface CollectionFieldPathCollection {
	id: GlobalCollectionId;
	primitiveFields: readonly CollectionFieldPathPrimitiveField[];
	relationFields?: readonly CollectionFieldPathRelationField[];
}

export interface CollectionFieldPathBuildOptions {
	includeConstrainedFields?: boolean;
	includeRelationFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	label?: 'displayName' | 'apiName';
	order?: 'label' | 'schema';
	terminal?: CollectionFieldPathTerminal;
	resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined;
	getFieldDisplayName: (field: CollectionFieldPathField) => string;
	/** When set, prune field paths the predicate rejects. Activates an eager, cycle-safe walk. */
	filterFieldPath?: (fieldPath: CollectionFieldPath) => boolean;
	/** Depth the eager filter walk materializes before deferring to lazy expansion. Defaults to 15. */
	maxDepth?: number;
}

interface ResolvedCollectionFieldPathBuildOptions extends
	Required<Pick<CollectionFieldPathBuildOptions, 'includeConstrainedFields' | 'includeRelationFields' | 'includeListRelationFields'
	| 'includeListRelationTerminalFields' | 'label' | 'order' | 'terminal' | 'getFieldDisplayName' | 'maxDepth'>>,
	Pick<CollectionFieldPathBuildOptions, 'resolveRelationCollection' | 'filterFieldPath'> {}

export interface CollectionFieldPathResolveOptions<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'> {
	includeConstrainedFields?: boolean;
	includeRelationFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	terminal?: TTerminal;
	resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined;
}

export interface CollectionFieldPathListOptions<TTerminal extends CollectionFieldPathTerminal | undefined = CollectionFieldPathTerminal> {
	includeConstrainedFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	terminal?: TTerminal;
	maxDepth?: number;
}

export interface CollectionFieldPathSearchEntry {
	displayPathLabels: string[];
	apiPathLabels: string[];
	leafDisplayLabel: string;
	leafApiName: string;
	depth: number;
}

export type CollectionFieldPathItemKind = 'primitive' | 'single-relation' | 'list-relation';

export interface CollectionFieldPathItem extends NestedSelectMenuItem {
	id: GlobalFieldId;
	apiName: string;
	displayName: string;
	fieldPathKind: CollectionFieldPathItemKind;
	searchText?: string;
	children?: CollectionFieldPathItem[] | (() => CollectionFieldPathItem[]);
}

interface CollectionFieldPathFlattenedItem extends FlattenedItem {
	item: CollectionFieldPathItem;
	path: CollectionFieldPathItem[];
}

export interface ResolvedCollectionFieldPathSegment {
	collection: CollectionFieldPathCollection;
	field: CollectionFieldPathField;
}

function terminalAllowsSingleRelation(terminal: CollectionFieldPathTerminal): boolean {
	return terminal === 'primitive-or-single-relation' || terminal === 'primitive-or-relation';
}

function terminalAllowsListRelation(terminal: CollectionFieldPathTerminal): boolean {
	return terminal === 'primitive-or-relation';
}

function isRelationSelectableTerminal(
	field: Pick<CollectionFieldPathRelationField, 'isList'>,
	options: { terminal: CollectionFieldPathTerminal; includeListRelationTerminalFields: boolean },
): boolean {
	return field.isList
		? terminalAllowsListRelation(options.terminal) && options.includeListRelationTerminalFields
		: terminalAllowsSingleRelation(options.terminal);
}

function isRelationTraversable(
	field: Pick<CollectionFieldPathRelationField, 'isList'>,
	options: { includeListRelationFields: boolean },
): boolean {
	return !field.isList || options.includeListRelationFields;
}

/** The relation's target collection — embedded on the field, or resolved via the option callback. */
function resolveOppositeCollection(
	field: Pick<CollectionFieldPathRelationField, 'id' | 'oppositeCollection'>,
	options: { resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined },
): CollectionFieldPathCollection | undefined {
	return field.oppositeCollection ?? options.resolveRelationCollection?.(field.id);
}

/** Defaults the field-inclusion flags shared by every field-path walker (terminal-list defaults to list). */
function resolveIncludeFlags(options: {
	includeConstrainedFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
}) {
	const includeConstrainedFields = options.includeConstrainedFields ?? false;
	const includeListRelationFields = options.includeListRelationFields ?? false;
	const includeListRelationTerminalFields = options.includeListRelationTerminalFields ?? includeListRelationFields;
	return { includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields };
}

function getFieldLabel(
	field: CollectionFieldPathField,
	options: Required<Pick<CollectionFieldPathBuildOptions, 'label' | 'getFieldDisplayName'>>,
): string {
	if (options.label === 'apiName') return field.apiName;
	return options.getFieldDisplayName(field);
}

function getFieldSearchText(
	field: CollectionFieldPathField,
	getFieldDisplayName: CollectionFieldPathBuildOptions['getFieldDisplayName'],
): string {
	const displayLabel = getFieldDisplayName(field);

	return [displayLabel, field.apiName]
		.filter((value, index, values) => values.indexOf(value) === index)
		.join(' ');
}

function buildPrimitiveFieldItem(
	field: CollectionFieldPathPrimitiveField,
	options: Required<Pick<CollectionFieldPathBuildOptions, 'label' | 'getFieldDisplayName'>>,
): CollectionFieldPathItem {
	return {
		id: field.id,
		apiName: field.apiName,
		displayName: options.getFieldDisplayName(field),
		fieldPathKind: 'primitive',
		label: getFieldLabel(field, options),
		icon: getPrimitiveTypeIcon(field.type),
		searchText: getFieldSearchText(field, options.getFieldDisplayName),
	};
}

/**
 * Builds the menu item for a single relation field — a branch into its opposite collection.
 *
 * Without `filterFieldPath` the item carries a lazy `children` thunk, so the nested menu expands
 * the opposite collection only when the user drills in (this is what keeps self-referential
 * relations traversable without materialising an infinite tree).
 *
 * With `filterFieldPath` the children are filtered and pruned eagerly so dead branches never
 * render. A relation re-entering a collection already on `path` is kept but deferred to a lazy
 * thunk, bounding the eager walk on self-referential / cyclic schemas.
 *
 * Returns `null` when the relation (and its surviving subtree) should not appear at all.
 */
function buildRelationFieldItem(
	field: CollectionFieldPathRelationField,
	options: ResolvedCollectionFieldPathBuildOptions,
	prefix: GlobalSingleRelationFieldId[],
	visited: Set<GlobalCollectionId>,
): CollectionFieldPathItem | null {
	if (!options.includeRelationFields) return null;
	if (field.isList && !options.includeListRelationFields && !options.includeListRelationTerminalFields) return null;

	const oppositeCollection = resolveOppositeCollection(field, options);

	if (!oppositeCollection && (!field.isList || options.includeListRelationFields)) return null;

	const traversable = oppositeCollection && isRelationTraversable(field, options)
		? oppositeCollection
		: undefined;

	const selectable = isRelationSelectableTerminal(field, options);

	const item: CollectionFieldPathItem = {
		id: field.id,
		apiName: field.apiName,
		displayName: options.getFieldDisplayName(field),
		fieldPathKind: field.isList ? 'list-relation' : 'single-relation',
		label: getFieldLabel(field, options),
		icon: field.isList ? 'lucide:list' : 'lucide:link',
		searchText: getFieldSearchText(field, options.getFieldDisplayName),
		selectable,
	};

	if (!options.filterFieldPath) {
		return traversable
			? { ...item, children: () => buildCollectionFieldPathItemsInternal(traversable, options) }
			: item;
	}

	const path = [...prefix, field.id] as CollectionFieldPath;
	// A non-traversable relation (a leaf) is always a terminal; a traversable one only if its terminal kind allows it.
	const isLeafOrSelectableTerminal = !traversable || selectable;
	const keepAsTerminal = isLeafOrSelectableTerminal && options.filterFieldPath(path) !== false;

	if (!traversable) {
		return keepAsTerminal ? item : null;
	}

	// Cycle: re-entering a collection already on the path. Keep it expandable but defer to lazy
	// expansion rather than eagerly recursing (which would materialise branching^depth nodes).
	if (visited.has(traversable.id)) {
		return {
			...item,
			selectable: keepAsTerminal,
			children: () => buildFilteredRelationChildren(traversable, options, path, visited),
		};
	}

	const filteredChildren = buildFilteredRelationChildren(traversable, options, path, new Set([...visited, traversable.id]));

	if (!keepAsTerminal && filteredChildren.length === 0) return null;

	return {
		...item,
		selectable: keepAsTerminal,
		children: filteredChildren.length > 0 ? () => filteredChildren : undefined,
	};
}

/** Filtered children for a relation branch, capped at `maxDepth` so deep or cyclic graphs stay bounded. */
function buildFilteredRelationChildren(
	collection: CollectionFieldPathCollection,
	options: ResolvedCollectionFieldPathBuildOptions,
	path: CollectionFieldPath,
	visited: Set<GlobalCollectionId>,
): CollectionFieldPathItem[] {
	if (path.length >= options.maxDepth) return [];
	return buildCollectionFieldPathItemsInternal(collection, options, path as GlobalSingleRelationFieldId[], visited);
}

function sortItems(items: CollectionFieldPathItem[]): CollectionFieldPathItem[] {
	return items.toSorted((left, right) => left.label.localeCompare(right.label));
}

/**
 * Recursive worker that builds one menu level: primitive leaves first, then relation branches.
 * `prefix` is the path of relation fields taken to reach this level (used to build the path passed
 * to `filterFieldPath`); `visited` holds the collections already on that path so a relation pointing
 * back into one is recognised as a cycle.
 */
function buildCollectionFieldPathItemsInternal(
	collection: CollectionFieldPathCollection,
	options: ResolvedCollectionFieldPathBuildOptions,
	prefix: GlobalSingleRelationFieldId[] = [],
	visited: Set<GlobalCollectionId> = new Set([collection.id]),
): CollectionFieldPathItem[] {
	const items: CollectionFieldPathItem[] = [];

	for (const field of collection.primitiveFields) {
		if (!options.includeConstrainedFields && isConstrainedPrimitiveField(field)) continue;
		if (options.filterFieldPath && options.filterFieldPath([...prefix, field.id] as CollectionFieldPath) === false) continue;
		items.push(buildPrimitiveFieldItem(field, options));
	}

	for (const field of collection.relationFields ?? []) {
		const relationItem = buildRelationFieldItem(field, options, prefix, visited);

		if (relationItem) {
			items.push(relationItem);
		}
	}

	return options.order === 'label' ? sortItems(items) : items;
}

/**
 * Builds the nested field-path menu tree that `CollectionFieldPathSelect` feeds to `DNestedSelectMenu`.
 *
 * Every collection level lists primitive fields as selectable leaves and single/list relations as
 * branches into their opposite collection. Relation `children` are lazy thunks the menu forces as
 * the user expands, so self-referential relations stay traversable without building an infinite tree.
 *
 * Passing `filterFieldPath` switches to an eager, cycle-safe walk that prunes rejected paths;
 * `maxDepth` bounds it (default 15).
 */
export function buildCollectionFieldPathItems(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathBuildOptions,
): CollectionFieldPathItem[] {
	return buildCollectionFieldPathItemsInternal(collection, {
		...resolveIncludeFlags(options),
		includeRelationFields: options.includeRelationFields ?? true,
		label: options.label ?? 'displayName',
		order: options.order ?? 'label',
		terminal: options.terminal ?? 'primitive',
		maxDepth: options.maxDepth ?? DEFAULT_FIELD_PATH_MAX_DEPTH,
		resolveRelationCollection: options.resolveRelationCollection,
		filterFieldPath: options.filterFieldPath,
		getFieldDisplayName: options.getFieldDisplayName,
	});
}

function isConstrainedPrimitiveField(field: CollectionFieldPathPrimitiveField): boolean {
	return Array.isArray(field.constrainedBy) && field.constrainedBy.length > 0;
}

function walkCollectionFieldPath<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'>(
	collection: CollectionFieldPathCollection,
	selectedPath: string[],
	options: CollectionFieldPathResolveOptions<TTerminal> = {},
): ResolvedCollectionFieldPathSegment[] | null {
	if (selectedPath.length === 0) return null;

	const { includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields } = resolveIncludeFlags(options);
	const includeRelationFields = options.includeRelationFields ?? true;
	const terminal = options.terminal ?? 'primitive';
	let currentCollection = collection;
	const segments: ResolvedCollectionFieldPathSegment[] = [];

	for (const [index, fieldId] of selectedPath.entries()) {
		const isTerminal = index === selectedPath.length - 1;
		const primitiveField = currentCollection.primitiveFields.find((field) => field.id === fieldId);

		if (primitiveField) {
			if (!isTerminal) return null;
			if (!includeConstrainedFields && isConstrainedPrimitiveField(primitiveField)) return null;

			segments.push({ collection: currentCollection, field: primitiveField });
			return segments;
		}

		const relationField = currentCollection.relationFields?.find((field) => field.id === fieldId);

		if (!relationField) return null;
		if (!includeRelationFields) return null;

		if (isTerminal) {
			if (!isRelationSelectableTerminal(relationField, { terminal, includeListRelationTerminalFields })) return null;

			segments.push({ collection: currentCollection, field: relationField });
			return segments;
		}

		if (!isRelationTraversable(relationField, { includeListRelationFields })) return null;

		const oppositeCollection = resolveOppositeCollection(relationField, options);

		if (!oppositeCollection) return null;

		segments.push({ collection: currentCollection, field: relationField });
		currentCollection = oppositeCollection;
	}

	return null;
}

export function resolveCollectionFieldPathSelection<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'>(
	collection: CollectionFieldPathCollection,
	selectedPath: string[],
	options: CollectionFieldPathResolveOptions<TTerminal> = {},
): CollectionFieldPathForTerminal<TTerminal> | null {
	return walkCollectionFieldPath(collection, selectedPath, options)
		? selectedPath as CollectionFieldPathForTerminal<TTerminal>
		: null;
}

export function fieldIdToCollectionFieldPath(fieldId: GlobalPrimitiveFieldId | GlobalSingleRelationFieldId): CollectionFieldPath {
	return [fieldId] as CollectionFieldPath;
}

export function toCollectionFieldPathKey(fieldPath: CollectionFieldPath): string {
	return fieldPath.join('.');
}

export function resolveCollectionFieldPath(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	overrides?: Partial<CollectionFieldPathResolveOptions<'primitive-or-relation'>>,
): ResolvedCollectionFieldPathSegment[] | null {
	return walkCollectionFieldPath(collection, fieldPath, {
		includeConstrainedFields: true,
		includeListRelationFields: false,
		includeListRelationTerminalFields: true,
		terminal: 'primitive-or-relation',
		...overrides,
	});
}

export function getCollectionFieldPathLabels(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	getFieldDisplayName: (field: CollectionFieldPathField) => string,
): string[] {
	// Labels are purely cosmetic — resolve through list relations so paths persisted before the
	// list-relation guard still render with human names instead of raw field ids.
	const segments = resolveCollectionFieldPath(collection, fieldPath, { includeListRelationFields: true });

	if (!segments) return fieldPath;

	return segments.map(({ field }) => getFieldDisplayName(field));
}

export function getCollectionFieldPathLabel(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	getFieldDisplayName: (field: CollectionFieldPathField) => string,
): string {
	return getCollectionFieldPathLabels(collection, fieldPath, getFieldDisplayName).join(' / ');
}

export function isCollectionFieldPathPermitted(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	canReadField: (collection: CollectionFieldPathCollection, field: CollectionFieldPathField) => boolean,
): boolean {
	const segments = resolveCollectionFieldPath(collection, fieldPath);

	if (!segments) return false;

	return segments.every(({ collection, field }) => canReadField(collection, field));
}

/**
 * Yields every selectable field path reachable from `collection`, depth-first and cycle-safe
 * (a per-branch `visited` set stops a path re-entering a collection it already contains, bounded
 * by `maxDepth`). Lazy by design: callers that only need the first match can stop pulling early.
 */
function* walkCollectionFieldPaths(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): Generator<CollectionFieldPath> {
	const { includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields } = resolveIncludeFlags(options);
	const terminal: CollectionFieldPathTerminal = options.terminal ?? 'primitive-or-single-relation';
	const maxDepth = options.maxDepth ?? DEFAULT_FIELD_PATH_MAX_DEPTH;
	const terminalOptions = { terminal, includeListRelationTerminalFields };

	function* walk(
		currentCollection: CollectionFieldPathCollection,
		prefix: GlobalSingleRelationFieldId[],
		visited: Set<GlobalCollectionId>,
	): Generator<CollectionFieldPath> {
		if (prefix.length >= maxDepth) return;

		for (const field of currentCollection.primitiveFields) {
			if (!includeConstrainedFields && isConstrainedPrimitiveField(field)) continue;
			yield [...prefix, field.id] as CollectionFieldPath;
		}

		for (const field of currentCollection.relationFields ?? []) {
			if (isRelationSelectableTerminal(field, terminalOptions)) {
				yield [...prefix, field.id] as CollectionFieldPath;
			}

			if (!isRelationTraversable(field, { includeListRelationFields })) continue;

			const oppositeCollection = field.oppositeCollection;

			if (!oppositeCollection) continue;
			if (visited.has(oppositeCollection.id)) continue;

			yield* walk(oppositeCollection, [...prefix, field.id], new Set([...visited, oppositeCollection.id]));
		}
	}

	yield* walk(collection, [], new Set([collection.id]));
}

export function listCollectionFieldPaths(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): CollectionFieldPath[] {
	return [...walkCollectionFieldPaths(collection, options)];
}

export function hasAnyHiddenCollectionFieldPath(
	collection: CollectionFieldPathCollection,
	isHidden: (fieldPath: CollectionFieldPath) => boolean,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): boolean {
	for (const fieldPath of walkCollectionFieldPaths(collection, options)) {
		if (isHidden(fieldPath)) return true;
	}

	return false;
}

function normalizeCollectionFieldPathTokenSource(value: string): string[] {
	return value
		.replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
		.toLowerCase()
		.split(/[\s.]+/)
		.filter(Boolean);
}

export function normalizeCollectionFieldPathQuery(query: string): string[] {
	return normalizeCollectionFieldPathTokenSource(query.trim());
}

function matchesOrderedTokenPrefixes(queryTokens: string[], candidateTokens: string[]): boolean {
	let candidateIndex = 0;

	for (const queryToken of queryTokens) {
		let matched = false;

		while (candidateIndex < candidateTokens.length) {
			if (candidateTokens[candidateIndex]!.startsWith(queryToken)) {
				matched = true;
				candidateIndex++;
				break;
			}

			candidateIndex++;
		}

		if (!matched) return false;
	}

	return true;
}

function isCollectionFieldPathFlattenedItem(item: FlattenedItem): item is CollectionFieldPathFlattenedItem {
	return item.path.every((segment) => isCollectionFieldPathItem(segment));
}

function isCollectionFieldPathItem(item: NestedSelectMenuItem): item is CollectionFieldPathItem {
	return typeof (item as Partial<CollectionFieldPathItem>).apiName === 'string'
		&& typeof (item as Partial<CollectionFieldPathItem>).displayName === 'string'
		&& isCollectionFieldPathItemKind((item as Partial<CollectionFieldPathItem>).fieldPathKind);
}

function isCollectionFieldPathItemKind(value: unknown): value is CollectionFieldPathItemKind {
	return value === 'primitive' || value === 'single-relation' || value === 'list-relation';
}

function matchesOrderedFuzzyTokens(queryTokens: string[], candidateTokens: string[]): boolean {
	let candidateIndex = 0;

	for (const queryToken of queryTokens) {
		const searchableTokens = candidateTokens
			.slice(candidateIndex)
			.map((token, index) => ({ token, index: candidateIndex + index }));

		if (searchableTokens.length === 0) return false;

		const match = new Fuse(searchableTokens, {
			ignoreLocation: true,
			threshold: 0.3,
			keys: ['token'],
		}).search(queryToken)[0];

		if (!match) return false;

		candidateIndex = match.item.index + 1;
	}

	return true;
}

export function searchCollectionFieldPathEntries<T>(
	items: T[],
	query: string,
	getEntry: (item: T) => CollectionFieldPathSearchEntry,
): T[] {
	const queryTokens = normalizeCollectionFieldPathQuery(query);

	if (queryTokens.length === 0) return items;

	const candidates = items.map((item) => {
		const entry = getEntry(item);

		return {
			item,
			entry,
			displayTokens: entry.displayPathLabels.flatMap((label) => normalizeCollectionFieldPathTokenSource(label)),
			apiTokens: entry.apiPathLabels.flatMap((label) => normalizeCollectionFieldPathTokenSource(label)),
		};
	});

	const matchesStrictQuery = (candidate: typeof candidates[number]) =>
		matchesOrderedTokenPrefixes(queryTokens, candidate.displayTokens)
		|| matchesOrderedTokenPrefixes(queryTokens, candidate.apiTokens);

	const matchesFuzzyQuery = (candidate: typeof candidates[number]) =>
		matchesOrderedFuzzyTokens(queryTokens, candidate.displayTokens)
		|| matchesOrderedFuzzyTokens(queryTokens, candidate.apiTokens);

	const strictMatches = candidates.filter((candidate) => matchesStrictQuery(candidate));
	const fuzzyMatches = strictMatches.length === 0
		? candidates.filter((candidate) => matchesFuzzyQuery(candidate))
		: [];
	const rankingPool = strictMatches.length > 0 ? strictMatches : fuzzyMatches;

	if (rankingPool.length === 0) return [];

	const normalizedQuery = queryTokens.join(' ');

	const ranked = new Fuse(
		rankingPool.map((candidate) => ({
			item: candidate.item,
			leafDisplayLabel: candidate.entry.leafDisplayLabel,
			leafApiName: candidate.entry.leafApiName,
			displayPathText: candidate.entry.displayPathLabels.join(' '),
			apiPathText: candidate.entry.apiPathLabels.join(' '),
		})),
		{
			ignoreLocation: true,
			threshold: strictMatches.length > 0 ? 0.4 : 0.3,
			keys: ['leafDisplayLabel', 'leafApiName', 'displayPathText', 'apiPathText'],
		},
	).search(normalizedQuery).map((result) => result.item.item);

	const orderedResults = ranked.length > 0 ? ranked : rankingPool.map((candidate) => candidate.item);

	return prioritizeTopLevelMatches(orderedResults, (item) => getEntry(item).depth === 1);
}

export function searchCollectionFieldPathItems(items: FlattenedItem[], query: string): FlattenedItem[] {
	const candidates = items.filter((item) => isCollectionFieldPathFlattenedItem(item));

	return searchCollectionFieldPathEntries(candidates, query, (item) => ({
		displayPathLabels: item.path.map((segment) => segment.displayName),
		apiPathLabels: item.path.map((segment) => segment.apiName),
		leafDisplayLabel: item.item.displayName,
		leafApiName: item.item.apiName,
		depth: item.path.length,
	}));
}
